import React, { FunctionComponent, useRef } from 'react';
import { Alignment, Button, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { FormTemplateOption } from 'data/template/template.types';
import { useTranslation } from 'react-i18next';
import { itemPredicate } from './advancedSelect.utils';
import advancedSelectItemRenderer from './advancedSelectItemRenderer';
import './advanced-select.scss';
import advancedSelectCreateItemRenderer from './advancedSelectCreateItemRenderer';

const AdvancedSelectType = Select.ofType<FormTemplateOption>();

type Props = {
  id?: string
  tabIndex?: number
  className?: string
  disabled?: boolean
  options: FormTemplateOption[]
  allowCreate?: boolean
  placeholder: string
  name: string
  value?: string
  'data-testid'?: string
  onChange?: (name: string, item: FormTemplateOption) => void
  onBlur?: (name: string) => void
  onCreateNew?: (name: string, item?: FormTemplateOption) => void
};

export const AdvancedSelect: FunctionComponent<Props> = ({
  id,
  tabIndex,
  className,
  disabled,
  placeholder,
  options,
  name,
  value,
  allowCreate,
  'data-testid': dataTestId,
  onChange = () => null,
  onBlur = () => null,
  onCreateNew = () => null,
}: Props) => {
  const isCreatingNew = useRef(false);
  const { t } = useTranslation();

  const createItem = (query: string): FormTemplateOption & { isNew?: true } => {
    return {
      label: query,
      value: query,
      isNew: true,
    };
  };

  let maybeCreateNewItemFromQuery: typeof createItem | undefined;
  let maybeCreateNewItemRenderer: typeof advancedSelectCreateItemRenderer | undefined;

  if (allowCreate) {
    maybeCreateNewItemFromQuery = createItem;
    maybeCreateNewItemRenderer = advancedSelectCreateItemRenderer;
  }

  const selectedItem = options.find(({ value: itemValue }) => itemValue === value);
  const text = selectedItem?.label ?? placeholder;

  const handleItemSelect = (item: FormTemplateOption & { isNew?: true }) => {
    if (item.isNew === true) {
      isCreatingNew.current = true;
      onCreateNew(name, item);
    } else {
      onChange(name, item);
    }
  };

  const handlePopupClose = (event?: React.SyntheticEvent<HTMLElement, Event>) => {
    if (isCreatingNew.current === true) {
      isCreatingNew.current = false;
      return;
    }

    if (event == null) {
      // The event was not triggered by the user, so should be ignored in this point.
      return;
    }

    onBlur(name);
  };

  return (
    <AdvancedSelectType
      data-testid={dataTestId}
      className={`advanced-select ${className}`}
      noResults={<MenuItem disabled text={t('common.form.noResults')} />}
      items={options}
      itemPredicate={itemPredicate}
      itemRenderer={advancedSelectItemRenderer}
      onItemSelect={handleItemSelect}
      // createNewItemPosition="first"
      // matchTargetWidth
      createNewItemFromQuery={maybeCreateNewItemFromQuery}
      createNewItemRenderer={maybeCreateNewItemRenderer}
      popoverProps={{
        minimal: true,
        fill: true,
        usePortal: false,
        wrapperTagName: 'div',
        onClose: handlePopupClose,
      }}
      inputProps={{
        className: 'advanced-select-input',
        placeholder: t('advancedSelect.filterPlaceholder'),
      }}
      resetOnSelect
      resetOnClose
      disabled={disabled}
    >
      <Button
        id={id}
        className="advanced-select-button"
        tabIndex={tabIndex}
        alignText={Alignment.LEFT}
        rightIcon="double-caret-vertical"
        text={text}
        disabled={disabled}
        value={value}
        fill
      />
    </AdvancedSelectType>
  );
};

export default AdvancedSelect;
